import React from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import {
  Alert,
  Form,
  Icon,
  Input,
  Button,
  Badge,
  Row,
  Col,
  Checkbox,
  DatePicker,
  Select,
  Radio,
  Modal,
  Tooltip,
  Switch,
  message,
} from "antd";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import { connect } from "react-redux";
import moment from "moment-timezone";

import FileLink from "../FileLink/FileLink";

import {
  updateSubjectRequest,
  createSubjectRequest,
  deleteSubjectRequest,
  activateSubjectRequest,
  lockSubjectRequest,
  unlockSubjectRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getPatientProfileReadRestrictedPermission,
  getPatientProfileCreatePermissionBySite,
  getPatientProfileUpdatePermissionBySite,
  getPatientProfileDeletePermissionBySite,
  getPatientProfileLockPermission,
  getPatientProfileUnlockPermission,
  getUserTimezone,
  getUserShortTimezone,
} from "../../reducers/user/selectors";
import { withParams } from "../../utils/url";

import classes from "./PatientProfileForm.css";

const routes = require("../../routes.json");

const { MonthPicker } = DatePicker;
const { Option } = Select;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some((field) => fieldsError[field]);
}

const emailRegex = new RegExp(
  /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi
);

class EventMessage extends React.Component {
  renderMessagePart(messagePart) {
    if (emailRegex.test(messagePart)) {
      return (
        <a href={`mailto:${messagePart}`} target="_blank">
          {messagePart}
        </a>
      );
    }

    return messagePart;
  }

  render() {
    return (
      <span>
        {this.props.message.split(emailRegex).map((messagePart, index) => (
          <React.Fragment key={index}>
            {this.renderMessagePart(messagePart)}
          </React.Fragment>
        ))}
      </span>
    );
  }
}

class PatientProfileForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isSubmitted: false,
      requestResult: {},
      deleteSubmitModal: false,
      activateSubmitModal: false,
      previewEvents: {},
      showPatientForm: true,
      showPatientEventSummary: true,
      showRegistryForm: false,
      confirmSubmitModal: false,
      locked: false,
      saveError: null,
      patientAgeFromServer: true,
    };
  }

  componentDidMount() {
    this.setState({
      locked: get(this.props.profile, "locked"),
    });
    if (this.props.profile && this.props.profile.age) {
      this.setState({
        patientAgeFromServer: !!this.props.profile.age,
      });
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.subjectID !== this.props.subjectID) {
      this.setState({
        requestResult: {},
        locked: get(this.props, "profile.locked"),
      });
    }
  }

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      const { getFieldValue } = this.props.form;
      const {
        profile,
        patientProfileCreatePermission,
        patientProfileUpdatePermission,
      } = this.props;

      this.setState({ isSubmitted: true, requestResult: {} });

      if (err) {
        this.setState({ saveError: "Error" });

        setTimeout(() => {
          this.setState({ saveError: null });
        }, 2000);
      }

      if (
        !err &&
        ((patientProfileCreatePermission && !profile) ||
          (patientProfileUpdatePermission && profile))
      ) {
        let patientAge = profile.age;
        if (!this.state.patientAgeFromServer || !patientAge) {
          const ageFromDate = this.props.randomization_date
            ? moment(this.props.randomization_date)
            : profile.created
            ? moment(profile.created)
            : moment();
          patientAge =
            getFieldValue("birthdate_month") && getFieldValue("birthdate")
              ? moment(ageFromDate).diff(
                  `${getFieldValue("birthdate")}-${getFieldValue(
                    "birthdate_month"
                  )}`,
                  "years"
                )
              : null;
        }

        if (
          (patientAge > 100 || patientAge < 18) &&
          !this.state.confirmSubmitModal
        ) {
          this.showConfirmModal();
          return;
        }

        this.setState({ isLoading: true });

        try {
          const profile = { ...values };
          if (values.birthdate && values.birthdate_month) {
            profile.birthdate = moment(
              `${getFieldValue("birthdate")}-${getFieldValue(
                "birthdate_month"
              )}`
            ).format("YYYY-MM-DD");
          }
          if (values.death_date) {
            profile.death_date = cloneDeep(values.death_date)
              .utc()
              .format("YYYY-MM-DDTHH:mm");
          }
          profile.is_consent_given = values.is_consent_given === "true";
          profile.is_deceased = values.is_deceased === "true";
          profile.is_subject_verified = values.is_subject_verified === true;
          profile.is_randomization_verified =
            values.is_randomization_verified === true;
          profile.age = patientAge;
          profile.contact_2_phone_number_type =
            profile.contact_2_phone_number_type || undefined;
          profile.contact_1_relationship_type =
            profile.contact_1_relationship_type || undefined;
          profile.contact_1_phone_number_type =
            profile.contact_1_phone_number_type || undefined;
          profile.contact_1_email = profile.contact_1_email || null;
          profile.contact_2_relationship_type =
            profile.contact_2_relationship_type || undefined;
          profile.contact_2_email = profile.contact_2_email || undefined;

          profile.randomizationDate = undefined;
          profile.subjectId = undefined;
          profile.birthdate_month = undefined;

          if (this.props.profile) {
            await updateSubjectRequest(this.props.subjectID, profile);
          } else {
            await createSubjectRequest(this.props.subjectID, profile);
          }
          this.setState({
            requestResult: {
              isLoading: false,
              type: "success",
              message: "Success",
            },
          });

          setTimeout(() => {
            this.hideConfirmModal();
            this.props.onSubmit(this.props.subjectID);
          }, 1500);
        } catch (e) {
          if (get(e, "response.status") === 401) {
            this.props.logout();
            return;
          }
          this.setState({
            isLoading: false,
            requestResult: {
              type: "error",
              message: get(e, "response.data.message", "Error"),
            },
          });

          if (get(e, "response.data.errors")) {
            const errors = {};
            Object.keys(get(e, "response.data.errors")).forEach(
              (errorFieldKey) => {
                errors[errorFieldKey] = {
                  value: values[errorFieldKey],
                  errors: get(e, `response.data.errors.${errorFieldKey}`).map(
                    (errorMessage) => new Error(errorMessage)
                  ),
                };
              }
            );
            this.props.form.setFields(errors);
          }
        }
      }
    });
  };

  showDeleteModal = () => {
    this.setState({ deleteSubmitModal: true });
  };
  hideDeleteModal = () => {
    this.setState({ deleteSubmitModal: false });
  };

  showConfirmModal = () => {
    this.setState({ confirmSubmitModal: true });
  };
  hideConfirmModal = () => {
    this.setState({ confirmSubmitModal: false });
  };

  showActivateModal = () => {
    this.setState({ activateSubmitModal: true });
  };
  hideActivateModal = () => {
    this.setState({ activateSubmitModal: false });
  };

  submitDeletePatient = async () => {
    const { patientProfileDeletePermission } = this.props;

    if (patientProfileDeletePermission) {
      try {
        await deleteSubjectRequest(this.props.subjectID);
        this.props.onProfileDelete();
      } catch (e) {
        if (get(e, "response.status") === 401) {
          this.props.logout();
        }
      }
    }
  };

  scrollUp = () => {
    window.scrollTo(0, 0);
  };

  togglePreviewEvents = (eventName) => () => {
    this.setState((state) => {
      const eventStatus = state.previewEvents[eventName] ? false : true;
      return {
        previewEvents: { ...state.previewEvents, [eventName]: eventStatus },
      };
    });
  };

  collapseSection = (sectionName) => () => {
    this.setState((state) => ({ [sectionName]: !state[sectionName] }));
  };

  activateSubject = async () => {
    const { patientProfileDeletePermission } = this.props;

    if (patientProfileDeletePermission) {
      try {
        await activateSubjectRequest(this.props.subjectID);
        this.props.onSubmit(this.props.subjectID);
      } catch (e) {
        if (get(e, "response.status") === 401) {
          this.props.logout();
        }
      }
    }
  };

  handleBDateChange = () => {
    this.setState({ patientAgeFromServer: false });
  };

  onSwitchLock = async () => {
    try {
      if (this.state.locked) {
        await unlockSubjectRequest(this.props.subjectID);
        this.setState({ locked: false });
      } else {
        await lockSubjectRequest(this.props.subjectID);
        this.setState({ locked: true });
      }
      message.success("Saved");
    } catch (e) {
      message.error(get(e, "response.data.error", "Error"));
    }
  };

  render() {
    const {
      isLoading,
      isSubmitted,
      requestResult,
      deleteSubmitModal,
      previewEvents,
      showPatientForm,
      showRegistryForm,
      showPatientEventSummary,
      activateSubmitModal,
      confirmSubmitModal,
      locked,
      saveError,
    } = this.state;

    const {
      profile,
      patientProfileCreatePermission,
      patientProfileUpdatePermission,
      patientProfileDeletePermission,
      state,
      patientProfileLockPermission,
      patientProfileUnlockPermission,
      patientProfileReadRestrictedPermission,
    } = this.props;
    const {
      getFieldDecorator,
      getFieldsError,
      getFieldError,
      isFieldTouched,
      getFieldValue,
    } = this.props.form;
    const subjectIdError =
      (isFieldTouched("subjectId") || isSubmitted) &&
      getFieldError("subjectId");
    const firstNameError =
      (isFieldTouched("first_name") || isSubmitted) &&
      getFieldError("first_name");
    const lastNameError =
      (isFieldTouched("last_name") || isSubmitted) &&
      getFieldError("last_name");
    const birthdateError =
      (isFieldTouched("birthdate") || isSubmitted) &&
      getFieldError("birthdate");
    const phoneNumber1Error =
      (isFieldTouched("phone_number_1") || isSubmitted) &&
      getFieldError("phone_number_1");
    const consentGivenError =
      (isFieldTouched("is_consent_given") || isSubmitted) &&
      getFieldError("is_consent_given");
    let emailError =
      (isFieldTouched("email") || isSubmitted) && getFieldError("email");
    emailError = emailError
      ? emailError[0] === "email is required"
        ? emailError
        : ["Email looks wrong"]
      : null;
    const ageFromDate = this.props.randomization_date
      ? moment(this.props.randomization_date)
      : get(profile, "created")
      ? moment(profile.created)
      : moment();
    let patientAge = get(profile, "age");
    if (!this.state.patientAgeFromServer || !patientAge) {
      patientAge =
        getFieldValue("birthdate") &&
        getFieldValue("birthdate_month") &&
        moment(ageFromDate).diff(
          `${getFieldValue("birthdate")}-${getFieldValue("birthdate_month")}`,
          "years"
        );
    }

    let isEditable =
      (patientProfileCreatePermission && !profile) ||
      (patientProfileUpdatePermission && profile && !locked);
    if (state !== "active") {
      isEditable = false;
    }

    return (
      <div className={classes.patientProfileForm}>
        <Form
          onSubmit={this.handleSubmit}
          className={classes.patientProfileFormWrapper}
        >
          <h4
            className={classnames("headline-default", classes.toggleHeadline)}
            onClick={this.collapseSection("showPatientForm")}
          >
            {!patientProfileReadRestrictedPermission && <Tooltip title={showPatientForm ? "collapse" : "show"}>
              <Icon
                type="arrow-up"
                className={classes.collapseIcon}
                style={{
                  fontSize: "20px",
                  transform: `rotate(${showPatientForm ? "0" : "180deg"})`,
                }}
              />
              &nbsp;
            </Tooltip>}
            <span className={classes.toggleText}>{this.props.subjectID}</span>
            &nbsp;
            <Badge
              count={profile ? "Edit" : "New"}
              style={{ backgroundColor: "#2968AE" }}
            />
            &nbsp;
            <Badge
              count={state}
              style={{
                backgroundColor: state === "active" ? "#49c850" : "#f00",
              }}
            />
            &nbsp;
            {profile && (
              <Badge
                count={locked ? "Locked" : "Unlocked"}
                style={{ backgroundColor: locked ? "#f00" : "#49c850" }}
              />
            )}
          </h4>

          {showPatientForm && (
            <div>
              {!patientProfileReadRestrictedPermission && (
                <div>
                  <Row gutter={8}>
                    <Col lg={8}>
                      <div className={classes.fieldInline}>
                        <label>
                          <b>Subject ID</b>
                          <Form.Item
                            validateStatus={subjectIdError ? "error" : ""}
                            help={subjectIdError || ""}
                          >
                            {getFieldDecorator("subjectId", {
                              initialValue: get(this.props, "code", ""),
                            })(<Input disabled />)}
                          </Form.Item>
                        </label>
                        <Form.Item>
                          {getFieldDecorator("is_subject_verified", {
                            rules: [{ required: true, message: "Required" }],
                            initialValue: get(
                              this.props,
                              "profile.is_subject_verified",
                              false
                            ),
                            valuePropName: "checked",
                          })(
                            <Checkbox disabled={!isEditable}>
                              Verified?
                            </Checkbox>
                          )}
                        </Form.Item>
                      </div>
                    </Col>

                    <Col lg={8}>
                      <div className={classes.fieldInline}>
                        <label>
                          <b>Randomization Date</b>
                          <Form.Item>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {getFieldDecorator("randomizationDate", {
                                initialValue: this.props.randomization_date
                                  ? moment
                                      .utc(this.props.randomization_date)
                                      .tz(
                                        this.props.userTimezone ||
                                          get(this.props, "site.time_zone") ||
                                          "UTC"
                                      )
                                  : null,
                              })(
                                <DatePicker
                                  format="YYYY/MM/DD HH:mm"
                                  showTime={{ format: "HH:mm" }}
                                  disabled
                                />
                              )}
                              &nbsp;
                              {this.props.userShortTimezone ||
                                get(this.props, "site.short_tz") ||
                                "UTC"}
                            </div>
                          </Form.Item>
                        </label>
                        <Form.Item>
                          {getFieldDecorator("is_randomization_verified", {
                            rules: [{ required: true, message: "Required" }],
                            initialValue: get(
                              this.props,
                              "profile.is_randomization_verified",
                              false
                            ),
                            valuePropName: "checked",
                          })(
                            <Checkbox disabled={!isEditable}>
                              Verified?
                            </Checkbox>
                          )}
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col md={8}>
                      <label>
                        <b>First name*</b>
                        <Form.Item
                          validateStatus={firstNameError ? "error" : ""}
                          help={firstNameError || ""}
                        >
                          {getFieldDecorator("first_name", {
                            rules: [{ required: true, message: "Required" }],
                            initialValue: get(
                              this.props,
                              "profile.first_name",
                              ""
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col md={8}>
                      <label>
                        <b>Last name*</b>
                        <Form.Item
                          validateStatus={lastNameError ? "error" : ""}
                          help={lastNameError || ""}
                        >
                          {getFieldDecorator("last_name", {
                            rules: [{ required: true, message: "Required" }],
                            initialValue: get(
                              this.props,
                              "profile.last_name",
                              ""
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col md={8}>
                      <label>
                        <b>Email*</b>
                        <Form.Item
                          validateStatus={emailError ? "error" : ""}
                          help={emailError || ""}
                        >
                          {getFieldDecorator("email", {
                            rules: [
                              {
                                required: true,
                                pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                              },
                            ],
                            initialValue: get(this.props, "profile.email"),
                          })(<Input disabled={!isEditable} type="email" />)}
                        </Form.Item>
                      </label>
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col md={3}>
                      <label>
                        <b>Birth year*</b>
                        <Form.Item
                          validateStatus={birthdateError ? "error" : ""}
                          help={birthdateError || ""}
                        >
                          {getFieldDecorator("birthdate", {
                            rules: [{ required: true }],
                            onChange: this.handleBDateChange,
                            initialValue: get(
                              get(this.props, "profile.birthdate")
                                ? get(this.props, "profile.birthdate").split(
                                    "-"
                                  )
                                : "",
                              "[0]"
                            ),
                          })(
                            <Input disabled={!isEditable} placeholder="yyyy" />
                          )}
                        </Form.Item>
                      </label>
                    </Col>
                    <Col md={3}>
                      <label>
                        <b>Birth month*</b>
                        <Form.Item>
                          {getFieldDecorator("birthdate_month", {
                            rules: [{ required: true, message: "Required" }],
                            onChange: this.handleBDateChange,
                            initialValue: get(
                              get(this.props, "profile.birthdate")
                                ? get(this.props, "profile.birthdate").split(
                                    "-"
                                  )
                                : "",
                              "[1]"
                            ),
                          })(
                            <Select disabled={!isEditable}>
                              <Option value="01">January</Option>
                              <Option value="02">February</Option>
                              <Option value="03">March</Option>
                              <Option value="04">April</Option>
                              <Option value="05">May</Option>
                              <Option value="06">June</Option>
                              <Option value="07">July</Option>
                              <Option value="08">August</Option>
                              <Option value="09">September</Option>
                              <Option value="10">October</Option>
                              <Option value="11">November</Option>
                              <Option value="12">December</Option>
                            </Select>
                          )}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col md={2}>
                      {!isNaN(patientAge) &&
                        patientAge !== undefined &&
                        patientAge < 130 &&
                        patientAge > 0 && (
                          <div>
                            <b>Age</b>
                            <div style={{ paddingTop: "10px" }}>
                              {patientAge}
                            </div>
                          </div>
                        )}
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col md={8}>
                      <div className={classes.fieldInline}>
                        <label style={{ flex: "0 0 120px" }}>
                          <b>Phone number (1)*</b>
                          <Form.Item>
                            {getFieldDecorator("phone_number_type_1", {
                              rules: [
                                {
                                  required: false,
                                  message: "Required",
                                },
                              ],
                              initialValue: get(
                                this.props,
                                "profile.phone_number_type_1",
                                "home"
                              ),
                            })(
                              <Select disabled={!isEditable}>
                                <Option value="home">Home</Option>
                                <Option value="cell">Cell</Option>
                                <Option value="work">Work</Option>
                                <Option value="other">Other</Option>
                              </Select>
                            )}
                          </Form.Item>
                        </label>
                        <label>
                          <b></b>
                          <Form.Item
                            validateStatus={phoneNumber1Error ? "error" : ""}
                            help={phoneNumber1Error || ""}
                          >
                            {getFieldDecorator("phone_number_1", {
                              rules: [{ required: true, message: "Required" }],
                              initialValue: get(
                                this.props,
                                "profile.phone_number_1"
                              ),
                            })(<Input disabled={!isEditable} type="tel" />)}
                          </Form.Item>
                        </label>
                      </div>
                    </Col>
                    <Col md={8}>
                      <div className={classes.fieldInline}>
                        <label style={{ flex: "0 0 120px" }}>
                          <b>Phone number (2)</b>
                          <Form.Item>
                            {getFieldDecorator("phone_number_type_2", {
                              initialValue: get(
                                this.props,
                                "profile.phone_number_type_2",
                                "home"
                              ),
                            })(
                              <Select disabled={!isEditable}>
                                <Option value="home">Home</Option>
                                <Option value="cell">Cell</Option>
                                <Option value="work">Work</Option>
                                <Option value="other">Other</Option>
                              </Select>
                            )}
                          </Form.Item>
                        </label>
                        <label>
                          <b></b>
                          <Form.Item>
                            {getFieldDecorator("phone_number_2", {
                              initialValue: get(
                                this.props,
                                "profile.phone_number_2",
                                ""
                              ),
                            })(<Input disabled={!isEditable} type="tel" />)}
                          </Form.Item>
                        </label>
                      </div>
                    </Col>
                    <Col md={8}>
                      <label>
                        <b>Consent given?</b>
                        <Form.Item
                          validateStatus={consentGivenError ? "error" : ""}
                          help={consentGivenError || ""}
                        >
                          {getFieldDecorator("is_consent_given", {
                            rules: [{ required: true, message: "Required" }],
                            initialValue: get(
                              this.props,
                              "has_consent_given",
                              get(this.props, "profile.is_consent_given" || "")
                            ).toString(),
                          })(
                            <Select disabled={true}>
                              <Option value="">Select</Option>
                              <Option value="true">Yes</Option>
                              <Option value="false">No</Option>
                            </Select>
                          )}
                        </Form.Item>
                      </label>
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col md={8}>
                      <div className={classes.fieldInline}>
                        <label>
                          <b>Patient Deceased?</b>
                          <Form.Item>
                            {getFieldDecorator("is_deceased", {
                              initialValue: get(
                                this.props,
                                "profile.is_deceased",
                                ""
                              ).toString(),
                            })(
                              <Radio.Group disabled={!isEditable}>
                                <Radio value="true">Yes</Radio>
                                <Radio value="false">No</Radio>
                              </Radio.Group>
                            )}
                          </Form.Item>
                        </label>
                      </div>
                    </Col>
                    <Col md={8}>
                      {getFieldValue("is_deceased") === "true" && (
                        <label>
                          <b>Decease date</b>
                          <Form.Item>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {getFieldDecorator("death_date", {
                                initialValue: get(
                                  this.props,
                                  "profile.death_date"
                                )
                                  ? moment
                                      .utc(this.props.profile.death_date)
                                      .tz(
                                        this.props.userTimezone ||
                                          get(this.props, "site.time_zone") ||
                                          "UTC"
                                      )
                                  : moment
                                      .utc(new Date())
                                      .tz(
                                        this.props.userTimezone ||
                                          get(this.props, "site.time_zone") ||
                                          "UTC"
                                      ),
                              })(
                                <DatePicker
                                  disabled={!isEditable}
                                  disabledDate={(currentDate) =>
                                    currentDate.diff(moment.now()) > 0
                                  }
                                  format="YYYY/MM/DD HH:mm"
                                  placeholder="yyyy-mm-dd HH:mm"
                                  showTime={{ format: "HH:mm" }}
                                />
                              )}
                              &nbsp;
                              {this.props.userShortTimezone ||
                                get(this.props, "site.short_tz") ||
                                "UTC"}
                            </div>
                          </Form.Item>
                        </label>
                      )}
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col md={8}>
                      <label>
                        <b>Street address</b>
                        <Form.Item>
                          {getFieldDecorator("street_address", {
                            initialValue: get(
                              this.props,
                              "profile.street_address"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>
                    <Col md={8}>
                      <label>
                        <b>City</b>
                        <Form.Item>
                          {getFieldDecorator("city", {
                            initialValue: get(this.props, "profile.city"),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>
                    <Col md={8}>
                      <label>
                        <b>Province</b>
                        <Form.Item>
                          {getFieldDecorator("province", {
                            initialValue: get(this.props, "profile.province"),
                          })(
                            <Select disabled={!isEditable}>
                              <Option value="">Select</Option>
                              <Option value="alberta">Alberta</Option>
                              <Option value="british_columbia">
                                British Columbia
                              </Option>
                              <Option value="manitoba">Manitoba</Option>
                              <Option value="new_brunswick">
                                New Brunswick
                              </Option>
                              <Option value="newfoundland_and_labrador">
                                Newfoundland and Labrador
                              </Option>
                              <Option value="nova_scotia">Nova Scotia</Option>
                              <Option value="ontario">Ontario</Option>
                              <Option value="prince_edward_island">
                                Prince Edward Island
                              </Option>
                              <Option value="quebec">Quebec</Option>
                              <Option value="saskatchewan">Saskatchewan</Option>
                              <Option value="northwest_territories">
                                Northwest Territories
                              </Option>
                              <Option value="nunavut">Nunavut</Option>
                              <Option value="Yukon">Yukon</Option>
                            </Select>
                          )}
                        </Form.Item>
                      </label>
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col md={8}>
                      <label>
                        <b>Postal code</b>
                        <Form.Item>
                          {getFieldDecorator("postal_code", {
                            initialValue: get(
                              this.props,
                              "profile.postal_code"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>
                  </Row>

                  <h3 className="headline-default">
                    Additional contact information
                  </h3>
                  <h4 className="headline-default">Other contact (1)</h4>

                  <Row gutter={8}>
                    <Col lg={6}>
                      <label>
                        <b>First name</b>
                        <Form.Item>
                          {getFieldDecorator("contact_1_first_name", {
                            initialValue: get(
                              this.props,
                              "profile.contact_1_first_name"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col lg={6}>
                      <label>
                        <b>Last name</b>
                        <Form.Item>
                          {getFieldDecorator("contact_1_last_name", {
                            initialValue: get(
                              this.props,
                              "profile.contact_1_last_name"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col
                      lg={
                        getFieldValue("contact_1_relationship_type") === "other"
                          ? 2
                          : 6
                      }
                    >
                      <label>
                        <b>Relationship</b>
                        <Form.Item>
                          {getFieldDecorator("contact_1_relationship_type", {
                            initialValue: get(
                              this.props,
                              "profile.contact_1_relationship_type"
                            ),
                          })(
                            <Select disabled={!isEditable}>
                              <Option value="">Select</Option>
                              <Option value="spouse_partner">
                                Spouse/ Partner
                              </Option>
                              <Option value="daughter">Daughter</Option>
                              <Option value="son">Son</Option>
                              <Option value="mother">Mother</Option>
                              <Option value="father">Father</Option>
                              <Option value="other">Other</Option>
                            </Select>
                          )}
                        </Form.Item>
                      </label>
                    </Col>

                    {getFieldValue("contact_1_relationship_type") ===
                      "other" && (
                      <Col lg={4}>
                        <label>
                          <b>&nbsp;</b>
                          <Form.Item>
                            {getFieldDecorator(
                              "contact_1_relationship_type_value",
                              {
                                initialValue: get(
                                  this.props,
                                  "profile.contact_1_relationship_type_value"
                                ),
                              }
                            )(<Input disabled={!isEditable} />)}
                          </Form.Item>
                        </label>
                      </Col>
                    )}

                    <Col lg={6}>
                      <b>Phone number (1)</b>
                      <div className={classes.fieldInline}>
                        <label style={{ flex: "0 0 70px" }}>
                          <Form.Item>
                            {getFieldDecorator("contact_1_phone_number_type", {
                              initialValue: get(
                                this.props,
                                "profile.contact_1_phone_number_type",
                                "home"
                              ),
                            })(
                              <Select disabled={!isEditable}>
                                <Option value="home">Home</Option>
                                <Option value="cell">Cell</Option>
                                <Option value="work">Work</Option>
                                <Option value="other">Other</Option>
                              </Select>
                            )}
                          </Form.Item>
                        </label>
                        <label>
                          <Form.Item>
                            {getFieldDecorator("contact_1_phone_number", {
                              initialValue: get(
                                this.props,
                                "profile.contact_1_phone_number"
                              ),
                            })(<Input disabled={!isEditable} type="tel" />)}
                          </Form.Item>
                        </label>
                      </div>
                    </Col>

                    <Col lg={6}>
                      <label>
                        <b>Email</b>
                        <Form.Item>
                          {getFieldDecorator("contact_1_email", {
                            initialValue: get(
                              this.props,
                              "profile.contact_1_email"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>
                  </Row>

                  <h4 className="headline-default">Other contact (2)</h4>
                  <Row gutter={8}>
                    <Col lg={6}>
                      <label>
                        <b>First name</b>
                        <Form.Item>
                          {getFieldDecorator("contact_2_first_name", {
                            initialValue: get(
                              this.props,
                              "profile.contact_2_first_name"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col lg={6}>
                      <label>
                        <b>Last name</b>
                        <Form.Item>
                          {getFieldDecorator("contact_2_last_name", {
                            initialValue: get(
                              this.props,
                              "profile.contact_2_last_name"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col
                      lg={
                        getFieldValue("contact_2_relationship_type") === "other"
                          ? 2
                          : 6
                      }
                    >
                      <label>
                        <b>Relationship</b>
                        <Form.Item>
                          {getFieldDecorator("contact_2_relationship_type", {
                            initialValue: get(
                              this.props,
                              "profile.contact_2_relationship_type"
                            ),
                          })(
                            <Select disabled={!isEditable}>
                              <Option value="">Select</Option>
                              <Option value="spouse_partner">
                                Spouse/ Partner
                              </Option>
                              <Option value="daughter">Daughter</Option>
                              <Option value="son">Son</Option>
                              <Option value="mother">Mother</Option>
                              <Option value="father">Father</Option>
                              <Option value="other">Other</Option>
                            </Select>
                          )}
                        </Form.Item>
                      </label>
                    </Col>

                    {getFieldValue("contact_2_relationship_type") ===
                      "other" && (
                      <Col lg={4}>
                        <label>
                          <b>&nbsp;</b>
                          <Form.Item>
                            {getFieldDecorator(
                              "contact_2_relationship_type_value",
                              {
                                initialValue: get(
                                  this.props,
                                  "profile.contact_2_relationship_type_value"
                                ),
                              }
                            )(<Input disabled={!isEditable} />)}
                          </Form.Item>
                        </label>
                      </Col>
                    )}

                    <Col lg={6}>
                      <b>Phone number (2):</b>
                      <div className={classes.fieldInline}>
                        <label style={{ flex: "0 0 70px" }}>
                          <Form.Item>
                            {getFieldDecorator("contact_2_phone_number_type", {
                              initialValue: get(
                                this.props,
                                "profile.contact_2_phone_number_type",
                                ""
                              ),
                            })(
                              <Select disabled={!isEditable}>
                                <Option value="home">Home</Option>
                                <Option value="cell">Cell</Option>
                                <Option value="work">Work</Option>
                                <Option value="other">Other</Option>
                              </Select>
                            )}
                          </Form.Item>
                        </label>
                        <label>
                          <Form.Item>
                            {getFieldDecorator("contact_2_phone_number", {
                              initialValue: get(
                                this.props,
                                "profile.contact_2_phone_number"
                              ),
                            })(<Input disabled={!isEditable} type="tel" />)}
                          </Form.Item>
                        </label>
                      </div>
                    </Col>

                    <Col lg={6}>
                      <label>
                        <b>Email</b>
                        <Form.Item>
                          {getFieldDecorator("contact_2_email", {
                            rules: [{ type: "email" }],
                            initialValue: get(
                              this.props,
                              "profile.contact_2_email"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>
                  </Row>

                  <h4 className="headline-default">Family Doctor</h4>
                  <Row gutter={8}>
                    <Col lg={6}>
                      <label>
                        <b>First name</b>
                        <Form.Item>
                          {getFieldDecorator("family_doctor_first_name", {
                            initialValue: get(
                              this.props,
                              "profile.family_doctor_first_name"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col lg={6}>
                      <label>
                        <b>Last name</b>
                        <Form.Item>
                          {getFieldDecorator("family_doctor_last_name", {
                            initialValue: get(
                              this.props,
                              "profile.family_doctor_last_name"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col lg={6}>
                      <label>
                        <b>Clinic name</b>
                        <Form.Item>
                          {getFieldDecorator("family_doctor_clinic_name", {
                            initialValue: get(
                              this.props,
                              "profile.family_doctor_clinic_name"
                            ),
                          })(<Input disabled={!isEditable} />)}
                        </Form.Item>
                      </label>
                    </Col>

                    <Col lg={6}>
                      <b>Phone number (1)</b>
                      <div className={classes.fieldInline}>
                        <label style={{ flex: "0 0 70px" }}>
                          <Form.Item>
                            {getFieldDecorator(
                              "family_doctor_phone_number_type",
                              {
                                initialValue: get(
                                  this.props,
                                  "profile.family_doctor_phone_number_type",
                                  "home"
                                ),
                              }
                            )(
                              <Select disabled={!isEditable}>
                                <Option value="home">Home</Option>
                                <Option value="cell">Cell</Option>
                                <Option value="work">Work</Option>
                                <Option value="other">Other</Option>
                              </Select>
                            )}
                          </Form.Item>
                        </label>
                        <label>
                          <Form.Item>
                            {getFieldDecorator("family_doctor_phone_number", {
                              initialValue: get(
                                this.props,
                                "profile.family_doctor_phone_number"
                              ),
                            })(<Input disabled={!isEditable} type="tel" />)}
                          </Form.Item>
                        </label>
                      </div>
                    </Col>

                    <Col lg={12}>
                      <b>Comments:</b>
                      <div>
                        <label>
                          <Form.Item>
                            {getFieldDecorator("comments", {
                              initialValue: get(this.props, "profile.comments"),
                            })(
                              <Input.TextArea disabled={!isEditable} rows="6" />
                            )}
                          </Form.Item>
                        </label>
                      </div>
                    </Col>
                  </Row>

                  {requestResult.type && (
                    <Alert
                      message={requestResult.message}
                      type={requestResult.type}
                      className={classes.submitResult}
                    />
                  )}

                  <Form.Item className={classes.formActions}>
                    {this.props.profile && (
                      <Tooltip
                        placement="topLeft"
                        title="Lock / Unlock the Subject Profile"
                      >
                        Lock status: &nbsp;
                        <Switch
                          disabled={
                            (!patientProfileUnlockPermission && locked) ||
                            (!patientProfileLockPermission && !locked)
                          }
                          checked={!locked}
                          onChange={this.onSwitchLock}
                        />
                      </Tooltip>
                    )}

                    {patientProfileDeletePermission && isEditable && (
                      <Button
                        type="primary"
                        size="large"
                        htmlType="button"
                        className={classes.deleteButton}
                        onClick={this.showDeleteModal}
                      >
                        Deactivate
                      </Button>
                    )}
                    {patientProfileDeletePermission && state !== "active" && (
                      <Button
                        type="primary"
                        size="large"
                        htmlType="button"
                        onClick={this.showActivateModal}
                      >
                        Activate
                      </Button>
                    )}
                    {isEditable && (
                      <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        className={classes.submitButton}
                      >
                        Save
                        {isLoading && <Icon type="loading" />}
                      </Button>
                    )}
                  </Form.Item>
                </div>
              )}
            </div>
          )}

          <br />

          <div>
            <h4
              className={classnames("headline-default", classes.toggleHeadline)}
              onClick={this.collapseSection("showPatientEventSummary")}
            >
              <Tooltip title={showPatientEventSummary ? "collapse" : "show"}>
                <Icon
                  type="arrow-up"
                  className={classes.collapseIcon}
                  style={{
                    fontSize: "20px",
                    transform: `rotate(${
                      showPatientEventSummary ? "0" : "180deg"
                    })`,
                  }}
                />{" "}
                &nbsp;
              </Tooltip>

              <span className={classes.toggleText}>Patient Event Summary</span>
            </h4>

            {showPatientEventSummary && (
              <table className={classes.table}>
                <thead>
                  <tr>
                    <td>Event</td>
                    <td>Status</td>
                    <td>Occurred at site</td>
                    <td>Data entered / Audit Trial</td>
                    <td>Actions</td>
                  </tr>
                </thead>
                <tbody>
                  {this.props.events_summary_report &&
                    this.props.events_summary_report.map((item, i) => (
                      <tr key={i}>
                        <td>{item.event}</td>
                        <td>
                          {item.status === "green" && (
                            <Icon
                              type="check-square"
                              className={classes.checkIcon}
                            />
                          )}
                          {item.status === "red" && (
                            <Icon
                              type="warning"
                              className={classes.warningIcon}
                            />
                          )}
                          {item.status === "orange" && (
                            <Icon
                              type="clock-circle"
                              className={classes.clockIcon}
                            />
                          )}
                          {item.status === "gray" && (
                            <Icon
                              type="clock-circle"
                              className={classes.checkIconGray}
                            />
                          )}
                          {item.status === "pending" && (
                            <Icon
                              type="clock-circle"
                              className={classes.checkIconGray}
                            />
                          )}
                        </td>
                        <td>{item.site}</td>
                        <td>
                          <div style={{ maxWidth: "500px" }}>
                            <p>
                              <EventMessage message={item.event_comments} />
                            </p>

                            {previewEvents[get(item, "event")] && (
                              <div>
                                {get(item, "other_details.messages", []).map(
                                  (message, index) => (
                                    <p key={index}>{message}</p>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className={classes.tableAction}>
                            {["follow_up_90_days"].includes(item.event) &&
                              state === "active" && (
                                <Link
                                  to={withParams(
                                    routes.admin.followUp90DaySingle,
                                    { subjectID: this.props.subjectID }
                                  )}
                                >
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["registry_id"].includes(item.event) &&
                              state === "active" && (
                                <Icon
                                  type="edit"
                                  theme="filled"
                                  onClick={this.scrollUp}
                                />
                              )}
                            {["sae"].includes(item.event) &&
                              state === "active" && (
                                <Link
                                  to={withParams(
                                    routes.admin.saeReportingSignle,
                                    {
                                      subjectID: this.props.subjectID,
                                    }
                                  )}
                                >
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["validation"].includes(item.event) &&
                              state === "active" && (
                                <Link to="/validate-randomization">
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}
                            {["deviation"].includes(item.event) &&
                              state === "active" && (
                                <Link to="/validate-randomization">
                                  <Icon type="edit" theme="filled" />
                                </Link>
                              )}

                            {get(item, "other_details.document_link") &&
                              !Array.isArray(
                                get(item, "other_details.document_link")
                              ) && (
                                <FileLink
                                  url={get(item, "other_details.document_link")}
                                />
                              )}
                            {get(item, "other_details.document_link") &&
                              Array.isArray(
                                get(item, "other_details.document_link")
                              ) &&
                              get(
                                item,
                                "other_details.document_link",
                                []
                              ).map((message, index) => (
                                <FileLink url={message.link} />
                              ))}
                            {get(item, "other_details.messages") && (
                              <Icon
                                type={
                                  previewEvents[get(item, "event")]
                                    ? "eye-invisible"
                                    : "eye"
                                }
                                theme="filled"
                                onClick={this.togglePreviewEvents(
                                  get(item, "event")
                                )}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>

          <br />

          <Row>
            <Col lg={12}>
              <b>Notes:</b>
              <div>
                <label>
                  <Form.Item>
                    {getFieldDecorator("notes", {
                      initialValue: get(this.props, "profile.notes"),
                    })(<Input.TextArea disabled={!isEditable} rows="6" />)}
                  </Form.Item>
                </label>
              </div>
            </Col>
          </Row>

          <Form.Item className={classes.formActionsNotes}>
            {isEditable && (
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                className={classes.submitButton}
              >
                Save
                {isLoading && <Icon type="loading" />}
              </Button>
            )}
          </Form.Item>
          {saveError && <Alert message={saveError} type="error" />}

          {requestResult.type && (
            <Alert
              message={requestResult.message}
              type={requestResult.type}
              className={classes.submitResult}
            />
          )}
        </Form>

        <Modal
          title="Confirm submit"
          visible={confirmSubmitModal}
          onOk={this.handleSubmit}
          onCancel={this.hideConfirmModal}
        >
          <p>Confirm submit action</p>
          <p>* Patient age looks wrong</p>

          {requestResult.type && (
            <Alert
              message={requestResult.message}
              type={requestResult.type}
              className={classes.submitResult}
            />
          )}
        </Modal>

        <Modal
          title="Subject deactivation"
          visible={deleteSubmitModal}
          onOk={this.submitDeletePatient}
          onCancel={this.hideDeleteModal}
        >
          <p>Confirm patient deactivate action</p>
        </Modal>

        <Modal
          title="Subject activation"
          visible={activateSubmitModal}
          onOk={this.activateSubject}
          onCancel={this.hideActivateModal}
        >
          <p>Confirm patient activate action</p>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  patientProfileCreatePermission: getPatientProfileCreatePermissionBySite(
    get(props, "site.id")
  )(state),
  patientProfileReadRestrictedPermission: getPatientProfileReadRestrictedPermission(
    state
  ),
  patientProfileUpdatePermission: getPatientProfileUpdatePermissionBySite(
    get(props, "site.id")
  )(state),
  patientProfileDeletePermission: getPatientProfileDeletePermissionBySite(
    get(props, "site.id")
  )(state),
  patientProfileLockPermission: getPatientProfileLockPermission(state),
  patientProfileUnlockPermission: getPatientProfileUnlockPermission(state),
});

const mapDispatchToProps = { logout };

export default Form.create({ name: "patientProfile" })(
  connect(mapStateToProps, mapDispatchToProps)(PatientProfileForm)
);



// WEBPACK FOOTER //
// ./src/components/PatientProfileForm/PatientProfileForm.js